import { Canvas } from '@react-three/fiber'
import { WebGLRenderer } from 'three'
import { Environment, Lightformer } from '@react-three/drei'
import { XR } from '@react-three/xr'
import { Physics, CuboidCollider } from '@react-three/rapier'
import { xrStore } from '../core/xr-store'
import { useInstances } from '../scene/instances'
import { Model } from '../scene/Model'
import { Grabbable } from '../features/grab/Grabbable'
import { useTrackXRPresses } from '../features/grab/pressTracking'
import { RealWorldColliders } from '../features/mr/RealWorldColliders'
import { HubMenu } from '../ui/HubMenu'
import { ModelMenu } from '../ui/ModelMenu'
import { DebugHud } from '../ui/DebugHud'
import { useMenuToggle } from '../ui/useMenuToggle'
import { Locomotion } from '../features/locomotion/Locomotion'
import { useDebugEnabled } from './debug'

interface SceneProps {
  onExit(): void
}

function SceneContents({ onExit }: SceneProps) {
  useTrackXRPresses()
  const [open, setOpen] = useMenuToggle()
  const instances = useInstances((s) => s.list)
  const setTransform = useInstances((s) => s.setTransform)
  const setScale = useInstances((s) => s.setScale)
  const debug = useDebugEnabled()

  return (
    <>
      <Locomotion />
      {instances.map((i) => (
        <Grabbable
          key={i.instanceId}
          assetId={i.assetId}
          position={i.position}
          quaternion={i.quaternion}
          scale={i.scale}
          physics={i.physics}
          onRelease={(t) => {
            setTransform(i.instanceId, t.position, t.quaternion)
            setScale(i.instanceId, t.scale)
          }}
        >
          <Model assetId={i.assetId} instanceId={i.instanceId} />
        </Grabbable>
      ))}

      <HubMenu open={open} onClose={() => setOpen(false)} onExit={onExit} />
      <ModelMenu />
      {debug && <DebugHud />}
    </>
  )
}

export function XRScene({ onExit }: SceneProps) {
  return (
    <Canvas
      camera={{ position: [0, 1.6, 1], fov: 70 }}
      // Create the WebGL2 context xr-compatible up front. Otherwise three calls the async
      // makeXRCompatible() right before a session, which loses the context on Meta headsets;
      // repeated losses get the page GPU-blocked by Chromium until a browser restart (three #30674).
      // R3F still applies its own color/tonemapping/shadow defaults to this renderer afterwards.
      gl={({ canvas }) => {
        const context = (canvas as HTMLCanvasElement).getContext('webgl2', {
          xrCompatible: true,
          antialias: true,
          alpha: true,
          powerPreference: 'high-performance',
        } as WebGLContextAttributes) as WebGL2RenderingContext | null
        const gl = new WebGLRenderer({ canvas, context: context ?? undefined })
        gl.localClippingEnabled = true // required by @react-three/uikit panels
        return gl
      }}
    >
      <XR store={xrStore}>
        <ambientLight intensity={1.2} />
        <hemisphereLight intensity={1} color="#ffffff" groundColor="#777777" />
        <directionalLight position={[2, 4, 2]} intensity={1.8} />
        {/* Procedural reflection map (no CDN): metallic/PBR imports render near-black without
            something to reflect. background={false} keeps passthrough visible. */}
        <Environment background={false} environmentIntensity={0.6} resolution={64}>
          <Lightformer intensity={2} position={[0, 4, 0]} scale={[8, 8, 1]} color="#ffffff" />
          <Lightformer intensity={1} position={[4, 1, 2]} scale={[4, 4, 1]} color="#cfe0ff" />
          <Lightformer intensity={1} position={[-4, 1, -2]} scale={[4, 4, 1]} color="#ffe6cf" />
        </Environment>
        <Physics gravity={[0, -9.81, 0]}>
          {/* Invisible floor at y=0 — the real floor under local-floor reference space, so dropped
              models rest on it (in MR they land on the physical floor seen through passthrough). */}
          <CuboidCollider args={[50, 0.05, 50]} position={[0, -0.05, 0]} />
          {/* Real-room scene meshes/planes as fixed colliders (Quest MR) — table, floor, walls. */}
          <RealWorldColliders />
          <SceneContents onExit={onExit} />
        </Physics>
      </XR>
    </Canvas>
  )
}
